import { useRef } from 'react';
import { DateValue, mergeProps, useDateSegment } from 'react-aria';
import { DateFieldState, DateSegment } from 'react-stately';

import BabyButton from '~/components/scales/BabyButton/BabyButton';
import ClearButton from '~/components/scales/ClearButton/ClearButton';
import ClearIconButton from '~/components/scales/ClearIconButton/ClearIconButton';
import Calendar from '~/components/scales/DateInput/Calendar';
import {
  ClearableProps,
  NotClearableProps,
  useDateInput,
} from '~/components/scales/DateInput/useDateInput';
import useHasClearButton from '~/components/scales/hooks/useHasClearButton';
import Icon from '~/components/scales/Icon/Icon';
import Popover from '~/components/scales/Popover/Popover';
import { StrictUnion } from '~/utilities/types';

type BaseProps = {
  'data-cy'?: string;
  defaultValue?: string | null;
  isDateUnavailable?: (date: DateValue) => boolean;
  isDisabled?: boolean;
  maxValue?: string | null;
  minValue?: string | null;
  unavailableMessage?: string;
  value?: string | null;
};

type LabelledProps = {
  'aria-label'?: string;
  label: string;
};
type UnlabelledProps = {
  'aria-label': string;
  label?: string;
};

type Props = BaseProps &
  StrictUnion<LabelledProps | UnlabelledProps> &
  StrictUnion<ClearableProps | NotClearableProps>;

export default function DateInput(props: Props) {
  const {
    buttonProps,
    calendarProps,
    errorMessage,
    errorMessageProps,
    fieldProps,
    fieldRef,
    fieldState,
    groupProps,
    groupState,
    isInvalid,
    labelProps,
    onClear,
    groupRef,
  } = useDateInput(props);

  const { hasInlineClearButton, hasTextClearButton } = useHasClearButton({
    isClearable: props.isClearable,
    isDisabled: props.isDisabled,
    label: props.label,
  });

  return (
    <div className="flex flex-col gap-0.5">
      {props.label && (
        <div className="flex">
          <label {...labelProps} className="mr-auto text-type-primary type-label">
            {props.label}
          </label>
          {hasTextClearButton && <ClearButton onClick={onClear} />}
        </div>
      )}
      <div
        {...groupProps}
        ref={groupRef}
        className={[
          'flex h-10 items-center gap-1 rounded-md border px-2 type-number focus-within:outline',
          isInvalid ? 'border-type-error' : ' ',
          props.isDisabled ? 'bg-button-inactive' : 'bg-background-primary',
        ].join(' ')}
        data-cy={props['data-cy'] ?? 'date-input'}
      >
        <BabyButton
          {...mergeProps(buttonProps)}
          aria-label="open calendar"
          icon={<Icon name="event" type="filled" />}
          isDisabled={props.isDisabled}
          onClick={groupState.toggle}
          type="default"
        />
        <div {...fieldProps} ref={fieldRef} className="inline-flex grow">
          {fieldState.segments.map((segment, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <DateFieldSegment key={i} segment={segment} state={fieldState} />
          ))}
        </div>
        {hasInlineClearButton && <ClearIconButton onClick={onClear} />}
      </div>
      {errorMessage && (
        <div {...errorMessageProps} className="cursor-default text-type-error type-label">
          {errorMessage}
        </div>
      )}
      {groupState.isOpen && (
        <Popover
          className="h-min w-min"
          placement="bottom start"
          state={groupState}
          triggerRef={groupRef}
        >
          <Calendar {...calendarProps} />
        </Popover>
      )}
    </div>
  );
}

function DateFieldSegment(props: { segment: DateSegment; state: DateFieldState }) {
  const {
    segment: { isEditable, isPlaceholder, text, type },
    state: { isDisabled },
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const { segmentProps } = useDateSegment(props.segment, props.state, ref);

  return (
    <div
      {...mergeProps(segmentProps)}
      ref={ref}
      aria-label={type} // can be 'month', 'day' 'year', or 'literal'
      className={[
        'group box-content rounded-md text-right outline-none',
        isEditable && !isDisabled
          ? 'decoration-selection-focus-outline decoration-2 underline-offset-2 focus-visible:underline'
          : 'cursor-default',
        isPlaceholder || isDisabled ? 'text-type-inactive' : 'text-type-primary',
      ].join(' ')}
    >
      {text}
    </div>
  );
}
